// Error types for API error handling
// Aligns with cpfgs-api GlobalExceptionHandler responses

/**
 * API Error Response structure from backend
 */
export interface ApiErrorResponse {
  timestamp: string
  status: number
  error: string
  message: string
  details?: string             // Additional error details from server
  errors?: Record<string, string>  // Field-level validation errors
  errorCode?: string               // RichMenu specific error codes
  importType?: string              // Import specific types
}

/**
 * RichMenu error codes from RichMenuException
 */
export type RichMenuErrorCode =
  | 'CREATE_FAILED'
  | 'UPDATE_FAILED'
  | 'DELETE_FAILED'
  | 'IMAGE_UPLOAD_FAILED'
  | 'NOT_FOUND'
  | 'INVALID_PAYLOAD'

/**
 * Import types from ImportException
 */
export type ImportType =
  | 'PRODUCT_CATALOG'
  | 'PRODUCT_CAMPAIGN'
  | 'PRODUCT_CONSULTANT'
  | 'SALES_OUT_DAILY'
  | 'SALES_TARGET'

/**
 * Custom API Error class with structured error information
 */
export class ApiError extends Error {
  timestamp: string
  status: number
  errorType: string
  details?: string
  errorCode?: string
  importType?: string
  fieldErrors?: Record<string, string>

  constructor(response: ApiErrorResponse) {
    super(response.message)
    this.name = 'ApiError'
    this.timestamp = response.timestamp || new Date().toISOString()
    this.status = response.status
    this.errorType = response.error
    this.details = response.details
    this.errorCode = response.errorCode
    this.importType = response.importType
    this.fieldErrors = response.errors
  }

  /**
   * Check if this is a validation error with field-level errors
   */
  isValidationError(): boolean {
    return this.status === 400 && !!this.fieldErrors && Object.keys(this.fieldErrors).length > 0
  }

  /**
   * Check if this is an authentication error (401)
   */
  isAuthenticationError(): boolean {
    return this.status === 401
  }

  /**
   * Check if this is an authorization/permission error (403)
   */
  isAuthorizationError(): boolean {
    return this.status === 403
  }

  /**
   * Check if this is a RichMenu specific error
   */
  isRichMenuError(): boolean {
    return !!this.errorCode
  }

  /**
   * Check if this is an Import specific error
   */
  isImportError(): boolean {
    return !!this.importType
  }

  /**
   * Check if this is a file too large error (413)
   */
  isFileTooLargeError(): boolean {
    return this.status === 413
  }

  /**
   * Check if this is a not found error (404)
   */
  isNotFoundError(): boolean {
    return this.status === 404
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500 && this.status < 600
  }
}
